import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Mail, Send, Settings, Check, AlertCircle, Loader2, Server,
  Shield, Clock, AlertTriangle, Wrench, FileText, Zap, Trash2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const EmailSettings = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingNotif, setSendingNotif] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const [formData, setFormData] = useState({
    host: "", port: 587, username: "", password: "",
    from_email: "", from_name: "ComplianceOS",
    use_tls: true, use_ssl: false
  });

  useEffect(() => {
    fetchConfig();
    fetchPresets();
  }, [token]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/smtp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setConfig(res.data);
        setFormData({
          host: res.data.host || "",
          port: res.data.port || 587,
          username: res.data.username || "",
          password: "",
          from_email: res.data.from_email || "",
          from_name: res.data.from_name || "ComplianceOS",
          use_tls: res.data.use_tls ?? true,
          use_ssl: res.data.use_ssl ?? false
        });
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const res = await axios.get(`${API}/smtp/presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPresets(res.data);
    } catch (error) {
      console.error("Failed to fetch presets:", error);
    }
  };

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      host: preset.host,
      port: preset.port,
      use_tls: preset.use_tls,
      use_ssl: preset.use_ssl
    }));
    toast.info(`${preset.name} preset applied. ${preset.notes}`);
  };

  const saveConfig = async () => {
    if (!formData.host || !formData.username || !formData.from_email) {
      toast.error("Host, username, and from email are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.password && config) {
        payload.password = "UNCHANGED";
      }
      await axios.post(`${API}/smtp/config`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("SMTP configuration saved");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Enter a test email address");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/smtp/test?to_email=${encodeURIComponent(testEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === "success") {
        toast.success(res.data.message);
        fetchConfig();
      } else {
        toast.error(`Test failed: ${res.data.error}`);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  const sendNotification = async (type) => {
    setSendingNotif(type);
    try {
      const res = await axios.post(`${API}/email/send-notification?notification_type=${type}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.sent > 0) {
        toast.success(`Notification sent (${res.data.sent} email(s))`);
      } else if (res.data.errors?.length > 0) {
        toast.error(`Failed: ${res.data.errors[0]}`);
      } else {
        toast.info("No notifications to send at this time");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send notification";
      toast.error(msg);
    } finally {
      setSendingNotif("");
    }
  };

  const deleteConfig = async () => {
    try {
      await axios.delete(`${API}/smtp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("SMTP configuration deleted");
      setConfig(null);
      setFormData({
        host: "", port: 587, username: "", password: "",
        from_email: "", from_name: "ComplianceOS",
        use_tls: true, use_ssl: false
      });
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="email-settings-page">
      <PageHeader
        title={t("email.title")}
        description={t("email.subtitle")}
        actions={config?.is_verified && (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 gap-1">
            <Check className="w-3 h-3" /> SMTP Verified
          </Badge>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SMTP Configuration */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Card className="border-border" data-testid="smtp-config-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  SMTP Server Configuration
                </CardTitle>
                <CardDescription>Connect to any SMTP provider to send notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Provider Presets */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Quick Setup - Select Provider</Label>
                  <div className="flex flex-wrap gap-2">
                    {presets.filter(p => p.host).map(preset => (
                      <Button key={preset.name} variant="outline" size="sm"
                        onClick={() => applyPreset(preset)}
                        className="text-xs"
                        data-testid={`preset-${preset.name.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Server Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>SMTP Host *</Label>
                    <Input value={formData.host} onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                      placeholder="smtp.gmail.com" data-testid="smtp-host-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input type="number" value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 587 })}
                      data-testid="smtp-port-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="your@email.com" data-testid="smtp-username-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input type="password" value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={config ? "Leave empty to keep current" : "App password or API key"}
                      data-testid="smtp-password-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Email *</Label>
                    <Input value={formData.from_email} onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                      placeholder="noreply@yourcompany.com" data-testid="smtp-from-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>From Name</Label>
                    <Input value={formData.from_name} onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                      placeholder="ComplianceOS" />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.use_tls}
                      onCheckedChange={(v) => setFormData({ ...formData, use_tls: v, use_ssl: v ? false : formData.use_ssl })}
                      data-testid="smtp-tls-switch" />
                    <Label className="text-sm">STARTTLS</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.use_ssl}
                      onCheckedChange={(v) => setFormData({ ...formData, use_ssl: v, use_tls: v ? false : formData.use_tls })}
                      data-testid="smtp-ssl-switch" />
                    <Label className="text-sm">SSL/TLS</Label>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button onClick={saveConfig} disabled={saving} data-testid="save-smtp-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    {saving ? "Saving..." : "Save Configuration"}
                  </Button>
                  {config && (
                    <Button variant="destructive" size="sm" onClick={deleteConfig} data-testid="delete-smtp-btn">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Test Email */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Card className="border-border" data-testid="test-email-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Test Configuration
                </CardTitle>
                <CardDescription>Send a test email to verify your SMTP settings work</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="recipient@example.com" className="flex-1"
                    data-testid="test-email-input" />
                  <Button onClick={sendTestEmail} disabled={testing || !config} data-testid="send-test-btn">
                    {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {testing ? "Sending..." : "Send Test"}
                  </Button>
                </div>
                {!config && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Save SMTP configuration first
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Notification Triggers */}
        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
            <Card className="border-border" data-testid="notification-triggers-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Send Notifications
                </CardTitle>
                <CardDescription>Trigger email notifications manually</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { type: "task_overdue", label: "Overdue Tasks", icon: Clock, desc: "Notify about overdue maintenance tasks" },
                  { type: "ca_overdue", label: "Overdue CAs", icon: Wrench, desc: "Notify about overdue corrective actions" },
                  { type: "compliance_alert", label: "Compliance Alert", icon: Shield, desc: "Alert if health score is below 70" },
                  { type: "weekly_digest", label: "Weekly Digest", icon: FileText, desc: "Send compliance summary report" },
                  { type: "all", label: "Send All", icon: Mail, desc: "Trigger all applicable notifications" }
                ].map(notif => (
                  <Button key={notif.type} variant="outline" className="w-full justify-start gap-3 h-auto py-3"
                    disabled={!config?.is_verified || sendingNotif === notif.type}
                    onClick={() => sendNotification(notif.type)}
                    data-testid={`send-notif-${notif.type}`}
                  >
                    {sendingNotif === notif.type
                      ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                      : <notif.icon className="w-4 h-4 flex-shrink-0" />}
                    <div className="text-left">
                      <p className="text-sm font-medium">{notif.label}</p>
                      <p className="text-xs text-muted-foreground">{notif.desc}</p>
                    </div>
                  </Button>
                ))}
                {!config?.is_verified && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 pt-2">
                    <AlertCircle className="w-3 h-3" /> Configure and verify SMTP to enable
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Config Status */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }}>
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Configured</span>
                  <Badge variant={config ? "default" : "secondary"}>{config ? "Yes" : "No"}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Verified</span>
                  <Badge variant={config?.is_verified ? "default" : "secondary"}>
                    {config?.is_verified ? "Yes" : "No"}
                  </Badge>
                </div>
                {config?.host && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Server</span>
                    <span className="font-mono text-xs">{config.host}:{config.port}</span>
                  </div>
                )}
                {config?.last_test_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Last Test</span>
                    <span className="text-xs">{new Date(config.last_test_at).toLocaleDateString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
